import React, { useId, useState } from 'react';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  TruckIcon,
} from '@/assets/icons/icons';
import type { LaneMode } from '../../../apis/lanePlanner';
import airplaneFlying from '../../../assets/images/ai-chat-airplane-flying.svg';
import { providerLogoFor } from '../../../common/utils/providerLogo';
import CarrierBadge from '../../../pages/Shipments/LanePlanner/CarrierBadge';
import {
  formatLocalDeparture,
  formatShortDate,
  laneSearchId,
  formatQuotePrice,
  formatTransitMinutes,
  normalizeLaneSearchData,
  routingAirports,
  routingStopsLabel,
  truckingLines,
} from '../chatActions';
import type {
  AIChatMessagePart,
  ChatActionResultPayload,
  LaneSearchLeg,
  LaneSearchQuote,
  LaneSearchTruckingLeg,
} from '../types';

const EMPTY_VALUE = '—';

const LANE_MODES: readonly string[] = ['air', 'ocean', 'road'];
const laneModeOf = (mode: string | null): LaneMode | null =>
  mode !== null && LANE_MODES.includes(mode) ? (mode as LaneMode) : null;

/** "SFO > JFK": two places with a chevron between them (never an arrow). */
const Places: React.FC<{ from: string | null; to: string | null }> = ({
  from,
  to,
}) => (
  <span className='ai-chat-drawer__quote-places'>
    <span>{from ?? EMPTY_VALUE}</span>
    <span aria-hidden='true' className='ai-chat-drawer__quote-chevron'>
      <ChevronRightIcon width={12} height={12} fill='currentColor' />
    </span>
    <span>{to ?? EMPTY_VALUE}</span>
  </span>
);

const LEG_TITLES: Record<string, string> = {
  air: 'Air',
  ocean: 'Ocean',
  road: 'Road',
};

/** The glyph for a way of travelling: the plane for a flight, the truck for a
 * road haul. Ocean has no glyph in the app yet, so it shows none. */
const ModeGlyph: React.FC<{ mode: string | null }> = ({ mode }) => {
  if (mode === 'air') {
    return <img src={airplaneFlying} alt='' />;
  }
  if (mode === 'road') {
    return <TruckIcon width={11} height={8} />;
  }
  return null;
};

/** The mode's glyph in a small round badge, at the left of a leg. */
const LegIcon: React.FC<{ mode: string | null }> = ({ mode }) => (
  <span className='ai-chat-drawer__quote-leg-icon' aria-hidden='true'>
    <ModeGlyph mode={mode} />
  </span>
);

/** A carrier as the Lane planner page shows it: its logo (the vendor's, else
 * the bundled one for the carrier's name) or its initials, in a circle, with
 * the mode's glyph at the lower right. */
const CarrierMark: React.FC<{
  mode: string | null;
  carrier: string | null;
  logoUrl: string | null;
}> = ({ mode, carrier, logoUrl }) => {
  const laneMode = laneModeOf(mode);
  const logo = logoUrl || providerLogoFor(carrier, laneMode);
  return (
    <span className='ai-chat-drawer__quote-mark'>
      <span className='ai-chat-drawer__quote-logo'>
        {logo ? (
          <img
            src={logo}
            alt=''
            onError={(event) => {
              event.currentTarget.style.display = 'none';
            }}
          />
        ) : (
          carrier &&
          laneMode && (
            <CarrierBadge
              name={carrier}
              mode={laneMode}
              className='ai-chat-drawer__quote-badge'
            />
          )
        )}
      </span>
      {(mode === 'air' || mode === 'road') && (
        <span className='ai-chat-drawer__quote-mode' aria-hidden='true'>
          <ModeGlyph mode={mode} />
        </span>
      )}
    </span>
  );
};

/** The mark at the left of a leg; a leg with no carrier keeps the plain glyph. */
const LegChip: React.FC<{
  mode: string | null;
  carrier: string | null;
  logoUrl: string | null;
}> = ({ mode, carrier, logoUrl }) =>
  carrier || logoUrl ? (
    <CarrierMark mode={mode} carrier={carrier} logoUrl={logoUrl} />
  ) : (
    <LegIcon mode={mode} />
  );

/** "10/30/26 5:00 AM" in the origin's own clock, or nothing. */
function whenText(iso: string | null): string | null {
  const when = formatLocalDeparture(iso);
  return when ? `${when.date.split(', ')[1]} ${when.time}` : null;
}

/** One flight, sailing or road haul of a quote. `price` is the leg's own
 * price when the quote prices this leg alone (a one-flight quote). */
const QuoteLeg: React.FC<{ leg: LaneSearchLeg; price: string | null }> = ({
  leg,
  price,
}) => {
  const departs = whenText(leg.departsAt);
  const arrives = whenText(leg.arrivesAt);
  const carrier = [leg.carrier, leg.serviceRef].filter(Boolean).join(' · ');
  return (
    <div className='ai-chat-drawer__quote-leg'>
      <LegChip mode={leg.mode} carrier={leg.carrier} logoUrl={leg.logoUrl} />
      <div className='ai-chat-drawer__quote-leg-main'>
        <span className='ai-chat-drawer__quote-leg-title'>
          {LEG_TITLES[leg.mode ?? ''] ?? 'Leg'}
        </span>
        <span className='ai-chat-drawer__quote-secondary'>
          <Places from={leg.origin} to={leg.destination} />
        </span>
        {(departs || arrives) && (
          <span className='ai-chat-drawer__quote-leg-times'>
            {departs && (
              <span className='ai-chat-drawer__quote-secondary'>
                Departs {departs}
              </span>
            )}
            {arrives && (
              <span className='ai-chat-drawer__quote-secondary'>
                Arrives {arrives}
              </span>
            )}
          </span>
        )}
        {carrier && (
          <span className='ai-chat-drawer__quote-secondary'>{carrier}</span>
        )}
      </div>
      <LegPrice price={price} />
    </div>
  );
};

/** A leg's price, with the note that it is an estimate. */
const LegPrice: React.FC<{ price: string | null }> = ({ price }) =>
  price ? (
    <div className='ai-chat-drawer__quote-leg-price'>
      <span className='ai-chat-drawer__quote-primary'>{price}</span>
      <span className='ai-chat-drawer__quote-secondary'>Estimated</span>
    </div>
  ) : null;

/** The pickup or the delivery trucking of a door-to-door quote. */
const QuoteTruckingLeg: React.FC<{
  title: string;
  leg: LaneSearchTruckingLeg;
}> = ({ title, leg }) => {
  const price = formatQuotePrice(leg.price, leg.currency);
  const travel = formatTransitMinutes(leg.transitMinutes);
  const carrier = [leg.carrier, leg.serviceLevel].filter(Boolean).join(' · ');
  return (
    <div className='ai-chat-drawer__quote-leg'>
      <LegChip mode='road' carrier={leg.carrier} logoUrl={null} />
      <div className='ai-chat-drawer__quote-leg-main'>
        <span className='ai-chat-drawer__quote-leg-title'>{title}</span>
        <span className='ai-chat-drawer__quote-secondary'>
          <Places from={leg.from} to={leg.to} />
        </span>
        {carrier && (
          <span className='ai-chat-drawer__quote-secondary'>{carrier}</span>
        )}
        {travel && (
          <span className='ai-chat-drawer__quote-secondary'>
            Travel time: {travel}
          </span>
        )}
      </div>
      <LegPrice price={price} />
    </div>
  );
};

/** What opens under a quote: the trucking and flight legs in order, the price
 * breakdown, and the quote's validity and reference. */
const QuoteDetails: React.FC<{ id: string; quote: LaneSearchQuote }> = ({
  id,
  quote,
}) => {
  const air = formatQuotePrice(quote.price, quote.currency);
  const pickup = quote.pickup
    ? formatQuotePrice(quote.pickup.price, quote.pickup.currency)
    : null;
  const delivery = quote.delivery
    ? formatQuotePrice(quote.delivery.price, quote.delivery.currency)
    : null;
  // With trucking the total is the whole trip, and is only shown once every
  // leg is priced; without trucking it is the quote's own price.
  const total = quote.trucking
    ? quote.trucking.status === 'complete'
      ? formatQuotePrice(
          quote.trucking.total,
          quote.trucking.currency ?? quote.currency
        )
      : null
    : air;
  const valid = formatShortDate(quote.validUntil);
  return (
    <div id={id} className='ai-chat-drawer__quote-details'>
      {quote.pickup && <QuoteTruckingLeg title='Pickup' leg={quote.pickup} />}
      {quote.legs.map((leg, index) => (
        <QuoteLeg
          // eslint-disable-next-line react/no-array-index-key -- legs have no id; the list is read-only and never reordered
          key={index}
          leg={leg}
          price={quote.legs.length === 1 ? air : null}
        />
      ))}
      {quote.delivery && (
        <QuoteTruckingLeg title='Delivery' leg={quote.delivery} />
      )}
      <dl className='ai-chat-drawer__quote-breakdown'>
        {pickup && (
          <div className='ai-chat-drawer__quote-breakdown-row'>
            <dt>Pickup trucking</dt>
            <dd>{pickup}</dd>
          </div>
        )}
        {quote.charges.map((charge, index) => (
          // eslint-disable-next-line react/no-array-index-key -- charge lines can repeat a label; the list is read-only
          <div key={index} className='ai-chat-drawer__quote-breakdown-row'>
            <dt>{charge.label ?? EMPTY_VALUE}</dt>
            <dd>
              {formatQuotePrice(charge.amount, quote.currency) ?? EMPTY_VALUE}
            </dd>
          </div>
        ))}
        {delivery && (
          <div className='ai-chat-drawer__quote-breakdown-row'>
            <dt>Delivery trucking</dt>
            <dd>{delivery}</dd>
          </div>
        )}
        {total && (
          <div className='ai-chat-drawer__quote-breakdown-row ai-chat-drawer__quote-breakdown-total'>
            <dt>Total</dt>
            <dd>{total}</dd>
          </div>
        )}
        {valid && (
          <div className='ai-chat-drawer__quote-breakdown-row ai-chat-drawer__quote-breakdown-note'>
            <dt>Quote valid until</dt>
            <dd>{valid}</dd>
          </div>
        )}
      </dl>
    </div>
  );
};

/** One quote, laid out as the Figma "S Card" (node 44242:80068): carrier
 * with its logo and mode badge, the vendor's reference, then departure,
 * duration and price columns. */
const LaneSearchQuoteCard: React.FC<{ quote: LaneSearchQuote }> = ({
  quote,
}) => {
  const departure = formatLocalDeparture(quote.departsAt);
  const price = formatQuotePrice(quote.price, quote.currency);
  const transit = formatTransitMinutes(quote.transitMinutes);
  const isAir = quote.mode === 'air';
  const isRoad = quote.mode === 'road';
  // A flight's second line is its stops; a road or ocean route is not a list
  // of airports, so those show the vendor's service level instead.
  const airports = routingAirports(quote.route);
  const stopsLabel = isAir || quote.mode === null;
  const detail = stopsLabel ? routingStopsLabel(airports) : quote.serviceLevel;
  // "Non stop" is only a fact when the vendor gave a schedule or a routing
  // with its stops; a route that is just the two ends says nothing.
  const detailKnown = !stopsLabel || transit !== null || airports.length > 2;
  const middle = transit ?? (detailKnown ? detail : null);
  const middleDetail = transit !== null && detailKnown ? detail : null;
  const trucking = truckingLines(quote.trucking);
  const [open, setOpen] = useState(false);
  const detailsId = useId();
  // Nothing to open on a quote that carries no legs, charges or trucking.
  const hasDetails =
    quote.legs.length > 0 ||
    quote.charges.length > 0 ||
    quote.pickup !== null ||
    quote.delivery !== null;
  // "See more" is the way on to the page; the chevron opens this quote's details.
  return (
    <article className='ai-chat-drawer__quote-card'>
      <div className='ai-chat-drawer__quote-header'>
        <div className='ai-chat-drawer__quote-title'>
          <CarrierMark
            mode={quote.mode}
            carrier={quote.carrier}
            logoUrl={quote.carrierLogoUrl}
          />
          <span className='ai-chat-drawer__quote-airline'>
            {quote.carrier ?? quote.vendor ?? EMPTY_VALUE}
          </span>
          {quote.mode && !isAir && !isRoad && (
            <span className='ai-chat-drawer__quote-mode-label'>
              {quote.mode}
            </span>
          )}
          {hasDetails && (
            <button
              type='button'
              className='ai-chat-drawer__quote-toggle'
              aria-expanded={open}
              aria-controls={detailsId}
              aria-label={open ? 'Hide quote details' : 'Show quote details'}
              onClick={() => setOpen((value) => !value)}
            >
              {open ? (
                <ChevronUpIcon size={16} color='currentColor' />
              ) : (
                <ChevronDownIcon size={16} color='currentColor' />
              )}
            </button>
          )}
        </div>
        {quote.ref && (
          <span className='ai-chat-drawer__quote-ref'>Ref {quote.ref}</span>
        )}
      </div>
      {(departure || middle || price) && (
        <div className='ai-chat-drawer__quote-columns'>
          {departure && (
            <div className='ai-chat-drawer__quote-column'>
              <span className='ai-chat-drawer__quote-primary'>
                {departure.time}
              </span>
              <span className='ai-chat-drawer__quote-secondary'>
                {departure.date}
              </span>
            </div>
          )}
          {middle && (
            <div className='ai-chat-drawer__quote-column'>
              <span className='ai-chat-drawer__quote-primary'>{middle}</span>
              {middleDetail && (
                <span className='ai-chat-drawer__quote-secondary'>
                  {middleDetail}
                </span>
              )}
            </div>
          )}
          {price && (
            <div className='ai-chat-drawer__quote-column ai-chat-drawer__quote-column--price'>
              <span className='ai-chat-drawer__quote-primary ai-chat-drawer__quote-price'>
                {price}
              </span>
              <span className='ai-chat-drawer__quote-secondary ai-chat-drawer__quote-estimate'>
                Estimated
              </span>
            </div>
          )}
        </div>
      )}
      {trucking.length > 0 && (
        <div className='ai-chat-drawer__quote-trucking-lines'>
          {trucking.map((line) => (
            <p key={line} className='ai-chat-drawer__quote-trucking'>
              {line}
            </p>
          ))}
        </div>
      )}
      {open && hasDetails && <QuoteDetails id={detailsId} quote={quote} />}
    </article>
  );
};

/** How a list-shaped action result is shown. Every list result gets the same
 * behavior: a short preview, then "See more" once there is something to see.
 * An action only says where its list is and where "See more" leads. */
type ListResultView = {
  /** Names the view: results of the same search share one name. */
  name: string;
  /** The list's accessible name. */
  label: string;
  /** How many items the chat shows before "See more". */
  preview: number;
  /** What identifies the search behind a result, read without the rest. */
  idOf: (data: unknown) => string | null;
  /** Reads the result: how many items it holds, the first `limit` of them
   * drawn, and whether the list is complete. */
  read: (
    data: unknown,
    limit: number
  ) => { count: number; shown: React.ReactNode[]; done: boolean };
  /** Shown instead of the list while it is empty. */
  emptyText: (done: boolean) => string;
  /** The in-app page that shows the whole list. */
  seeMorePath: (id: string) => string;
};

const LANE_SEARCH_VIEW: ListResultView = {
  name: 'lane_search',
  label: 'Lane search quotes',
  preview: 2,
  idOf: laneSearchId,
  read: (data, limit) => {
    const { done, quotes } = normalizeLaneSearchData(data);
    return {
      count: quotes.length,
      shown: quotes
        .slice(0, limit)
        .map((quote, index) => (
          <LaneSearchQuoteCard key={quote.quoteId ?? index} quote={quote} />
        )),
      done,
    };
  },
  emptyText: (done) => (done ? 'No quotes found.' : 'Searching for quotes…'),
  seeMorePath: (id) => `/lane-planner?search=${encodeURIComponent(id)}`,
};

// One view per action name -- an action with no entry here renders nothing
// (the model's text reply still covers it).
const LIST_VIEWS: Record<string, ListResultView> = {
  lane_search: LANE_SEARCH_VIEW,
  // Follow-up reads of the same search return the same shape.
  lane_search_results: LANE_SEARCH_VIEW,
};

const listViewFor = (action: string): ListResultView | undefined =>
  Object.hasOwn(LIST_VIEWS, action) ? LIST_VIEWS[action] : undefined;

const ListResult: React.FC<{
  view: ListResultView;
  data: unknown;
  onOpenLink?: (path: string) => void;
}> = ({ view, data, onOpenLink }) => {
  const { count, shown, done } = view.read(data, view.preview);
  const id = view.idOf(data);
  // "See more" leads to the whole list: offered once there is something to
  // show, or while the list is still being produced (the only way to follow
  // it). A finished empty list has nowhere useful to lead.
  const seeMorePath =
    id && onOpenLink && (count > 0 || !done) ? view.seeMorePath(id) : null;

  return (
    <section className='ai-chat-drawer__quotes' aria-label={view.label}>
      {count === 0 ? (
        <p className='ai-chat-drawer__quotes-empty'>{view.emptyText(done)}</p>
      ) : (
        shown
      )}
      {seeMorePath && onOpenLink && (
        <button
          type='button'
          className='ai-chat-drawer__quotes-more'
          onClick={() => onOpenLink(seeMorePath)}
        >
          See more →
        </button>
      )}
    </section>
  );
};

/** Names the search an action result belongs to, so a later snapshot of the
 * same search can replace an earlier one; null when it has no identity. */
function actionResultKey(result: ChatActionResultPayload): string | null {
  const view = listViewFor(result.action);
  const id = view?.idOf(result.data);
  return view && id ? `${view.name}:${id}` : null;
}

/** Positions of action results a later result of the same search replaces.
 * One search can answer several times in a reply (the first read often comes
 * back unfinished, a later one complete); only the latest is worth showing. */
export function supersededActionResultIndexes(
  parts: readonly AIChatMessagePart[]
): Set<number> {
  const seen = new Set<string>();
  const superseded = new Set<number>();
  // From the end, so the first sighting of a search is its latest result.
  for (let index = parts.length - 1; index >= 0; index -= 1) {
    const part = parts[index];
    const key =
      part.kind === 'action_result' ? actionResultKey(part.result) : null;
    if (key && seen.has(key)) superseded.add(index);
    else if (key) seen.add(key);
  }
  return superseded;
}

const ChatActionResult: React.FC<{
  result: ChatActionResultPayload;
  /** Opens an in-app path, for a result's "See more". */
  onOpenLink?: (path: string) => void;
}> = ({ result, onOpenLink }) => {
  const view = listViewFor(result.action);
  return view ? (
    <ListResult view={view} data={result.data} onOpenLink={onOpenLink} />
  ) : null;
};

export default ChatActionResult;
